import React, { useState } from 'react';
import { useAuth, AuthMethod } from '../auth/AuthContext';
import { SIGNAL_ENABLED } from '../siteConfig';

// Sign-in modal for edit mode. Email code (with a 4-emoji anti-phishing match)
// is the primary path; a passkey button appears when the device supports it.
// After a first successful email sign-in the editor is offered a passkey.
type Step = 'request' | 'verify' | 'passkey';

export function SignInPanel({ onClose }: { onClose: () => void }) {
  const {
    requestCode, verifyCode, isLocal,
    passkeySupported, registerPasskey, signInWithPasskey,
  } = useAuth();
  const [method, setMethod] = useState<AuthMethod>('email');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('request');
  const [hint, setHint] = useState('');
  const [emojis, setEmojis] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pkMsg, setPkMsg] = useState('');

  const methods: AuthMethod[] = SIGNAL_ENABLED ? ['email', 'signal'] : ['email'];

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const r = await requestCode(method, identifier);
    setBusy(false);
    if (r.ok) { setStep('verify'); setHint(r.hint ?? ''); setEmojis(r.emojis ?? []); }
    else setError(r.error ?? 'Something went wrong.');
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const r = await verifyCode(method, identifier, code);
    setBusy(false);
    if (!r.ok) { setError(r.error ?? 'That code didn’t work.'); return; }
    // Offer a passkey on first success (real backend only); else finish.
    if (passkeySupported && !isLocal) setStep('passkey');
    else onClose();
  }

  async function onPasskeyLogin() {
    setBusy(true); setError('');
    const r = await signInWithPasskey();
    setBusy(false);
    if (r.ok) onClose();
    else setError(r.error ?? 'Passkey sign-in failed.');
  }

  async function onRegisterPasskey() {
    setBusy(true); setPkMsg('');
    const r = await registerPasskey();
    setBusy(false);
    if (r.ok) { setPkMsg('Passkey saved — you can use it next time.'); setTimeout(onClose, 900); }
    else setPkMsg(r.error ?? 'Could not save the passkey.');
  }

  const idLabel = method === 'email' ? 'Email' : 'Signal number';
  const idType = method === 'email' ? 'email' : 'tel';
  const idPlaceholder = method === 'email' ? 'you@example.com' : '+1 555 123 4567';

  return (
    <div className="signin__overlay" onClick={onClose}>
      <div className="signin" role="dialog" aria-modal="true" aria-label="Sign in to edit" onClick={e => e.stopPropagation()}>
        <button className="signin__close" onClick={onClose} aria-label="Close">×</button>
        <h3>Sign in to edit</h3>

        {isLocal && (
          <p className="signin__note">
            Local preview — sign-in checks the allowlist only; edits save to
            this browser until you press Save.
          </p>
        )}

        {step === 'request' && (
          <>
            {passkeySupported && (
              <button type="button" className="btn signin__passkey" onClick={onPasskeyLogin} disabled={busy}>
                🔑 Sign in with a passkey
              </button>
            )}

            {methods.length > 1 && (
              <div className="signin__tabs" role="tablist">
                {methods.map(m => (
                  <button
                    key={m}
                    role="tab"
                    aria-selected={method === m}
                    className={method === m ? 'active' : ''}
                    onClick={() => { setMethod(m); setStep('request'); setError(''); setHint(''); }}
                  >
                    {m === 'email' ? 'Email' : 'Signal'}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={onRequest} className="signin__form">
              <label htmlFor="si-id">{idLabel}</label>
              <input
                id="si-id"
                type={idType}
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder={idPlaceholder}
                autoFocus
                required
              />
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? 'Sending…' : 'Email me a code'}
              </button>
            </form>
          </>
        )}

        {step === 'verify' && (
          <form onSubmit={onVerify} className="signin__form">
            {hint && <p className="signin__hint">{hint}</p>}
            {emojis.length > 0 && (
              <div className="signin__emoji">
                <span className="signin__emoji-label">These should match your email:</span>
                <span className="signin__emoji-row">{emojis.map((e, i) => <span key={i}>{e}</span>)}</span>
              </div>
            )}
            <label htmlFor="si-code">Code</label>
            <input
              id="si-code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="123456"
              autoFocus
              required
            />
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify & edit'}
            </button>
            <button type="button" className="signin__back" onClick={() => { setStep('request'); setError(''); setEmojis([]); }}>
              ← use a different {idLabel.toLowerCase()}
            </button>
          </form>
        )}

        {step === 'passkey' && (
          <div className="signin__form">
            <p className="signin__hint">
              You're signed in. Save a passkey on this device so next time you can
              sign in with Face ID / Touch ID / your PIN — no code needed.
            </p>
            <button type="button" className="btn btn--primary" onClick={onRegisterPasskey} disabled={busy}>
              {busy ? 'Setting up…' : '🔑 Save a passkey'}
            </button>
            <button type="button" className="signin__back" onClick={onClose}>Skip for now</button>
            {pkMsg && <p className="signin__hint">{pkMsg}</p>}
          </div>
        )}

        {error && <p className="signin__error" role="alert">{error}</p>}
      </div>
    </div>
  );
}
